import {
	AlertCircle,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	HelpCircle,
	Mic,
	Square,
	XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { AudioPlayer } from '#app/components/audio-player.tsx'
import {
	SimpleTranscript,
	type SimpleTranscriptSegment,
} from '#app/components/transcript.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '#app/components/ui/collapsible.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { Progress } from '#app/components/ui/progress.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '#app/components/ui/sheet.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import {
	useVoiceConnection,
	type ICSRData as FormData,
} from '#app/hooks/use-voice-connection.tsx'
import { type Route } from '#app/routes/hotline+/+types/_index.ts'
// Domain types are imported for future use when timeline data is converted

// Simplified interfaces for demo mock data
interface SimpleMedicalHistoryItem {
	id: string
	condition: string
	startDate?: Date
	endDate?: Date
	isOngoing?: boolean
}

interface SimpleConcomitantMedication {
	id: string
	name: string
	dose?: string
	frequency?: string
	indication?: string
	startDate?: Date
	endDate?: Date
	isOngoing?: boolean
}

// Extended form data with structured medical data
interface ExtendedFormData extends FormData {
	medicalHistoryItems?: SimpleMedicalHistoryItem[]
	concomitantMedications?: SimpleConcomitantMedication[]
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Voice Hotline Demo - Lydra AI' },
]

export default function HotlineRoute() {
	const [searchParams] = useSearchParams()
	const isMockMode = searchParams.get('mock') === 'true'
	const isFinalMode = searchParams.get('final') === 'true'

	const {
		isConnected,
		isConnecting,
		isRecording,
		transcript,
		icsrData: formData,
		startCall,
		endCall,
		error,
	} = useVoiceConnection()

	// Generate auto-populated administrative fields
	const generateInitialFormData = useCallback((): FormData => {
		return {
			caseId: 'CASE-' + Date.now().toString().slice(-6),
			reportDate: new Date().toISOString().split('T')[0],
			reportType: 'spontaneous',
		}
	}, [])

	// Mock mode state
	const [, setMockProgress] = useState(0)
	const [mockTranscript, setMockTranscript] = useState('')
	const [mockFormData, setMockFormData] = useState<FormData>(
		generateInitialFormData,
	)
	const [mockIsConnected, setMockIsConnected] = useState(false)
	const [mockIsRecording, setMockIsRecording] = useState(false)
	const [mockIsCompleted, setMockIsCompleted] = useState(false)
	const [mockAudioState, setMockAudioState] = useState({
		isPlaying: false,
		currentTime: 0,
		duration: 30000, // 30 seconds mock duration
	})
	const [showAudioPlayback, setShowAudioPlayback] = useState(false)

	const simulateConversation = useCallback(() => {
		const initialData = generateInitialFormData()

		// If final mode, set everything immediately
		if (isFinalMode) {
			const finalTranscript =
				"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?\n\nCaller: The patient is a 67-year-old female, initials are M.R. She weighs about 145 pounds.\n\nAgent: Thank you. Now, can you tell me about the adverse event? What happened and when did it occur?\n\nCaller: She developed severe cardiac arrhythmias approximately 3 hours after taking her morning dose of the study medication. The event occurred on March 15th. The arrhythmias were serious enough that we had to admit her to the ICU.\n\nAgent: I understand. Can you tell me about the medication she was taking? What was the product name, dose, and how was it administered?\n\nCaller: She was taking CardioMax 150mg tablets, twice daily by mouth. This was for the clinical trial she's participating in for her heart condition.\n\nAgent: Thank you for all this information, Dr. Martinez. I have captured all the key details for the adverse event report. Is there anything else you'd like to add about this case?"

			const finalData: ExtendedFormData = {
				...initialData,
				reporterName: 'Dr. Jennifer Martinez',
				reporterType: 'healthcare_professional' as const,
				reporterContact: "St. Mary's Hospital - Cardiology Department",
				patientAge: '67',
				patientSex: 'female' as const,
				patientWeight: '65.8',
				medicalHistory: 'Heart condition (clinical trial participant)',
				medicalHistoryItems: [
					{
						id: '1',
						condition: 'Coronary Artery Disease',
						startDate: new Date('2020-06-15'),
						isOngoing: true,
					},
					{
						id: '2',
						condition: 'Hypertension',
						startDate: new Date('2018-03-22'),
						isOngoing: true,
					},
					{
						id: '3',
						condition: 'Type 2 Diabetes',
						startDate: new Date('2019-11-08'),
						isOngoing: true,
					},
				],
				concomitantMedications: [
					{
						id: '1',
						name: 'Lisinopril',
						dose: '10mg',
						frequency: 'once daily',
						indication: 'Hypertension',
						startDate: new Date('2018-03-22'),
						isOngoing: true,
					},
					{
						id: '2',
						name: 'Metformin',
						dose: '500mg',
						frequency: 'twice daily',
						indication: 'Type 2 Diabetes',
						startDate: new Date('2019-11-08'),
						isOngoing: true,
					},
					{
						id: '3',
						name: 'Atorvastatin',
						dose: '20mg',
						frequency: 'once daily',
						indication: 'High Cholesterol',
						startDate: new Date('2020-01-15'),
						isOngoing: true,
					},
				],
				eventDescription: 'Severe cardiac arrhythmias',
				eventOnsetDate: '2024-03-15',
				severity: 'severe' as const,
				seriousness: true,
				outcome: 'recovering' as const,
				productName: 'CardioMax',
				dose: '150mg',
				indication: 'Heart condition',
				therapyStartDate: '2024-03-10',
				routeOfAdministration: 'oral',
				causalityAssessment: 'probable' as const,
			}

			// Set everything immediately
			setMockTranscript(finalTranscript)
			setMockProgress(100)
			setMockFormData(finalData)

			// Mark as completed and show audio playback after a short delay
			setTimeout(() => {
				setMockIsRecording(false)
				setMockIsCompleted(true)
				setShowAudioPlayback(true)
				setTimeout(() => {
					setMockIsConnected(false)
				}, 1000)
			}, 2000)

			return
		}

		// Normal progressive simulation
		const conversationSteps = [
			{
				delay: 2000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?",
				progress: 5,
				formData: {
					...initialData,
				},
			},
			{
				delay: 4000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.",
				progress: 15,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
				},
			},
			{
				delay: 7000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?",
				progress: 25,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
				},
			},
			{
				delay: 10000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?\n\nCaller: The patient is a 67-year-old female, initials are M.R. She weighs about 145 pounds.",
				progress: 40,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
					patientAge: '67',
					patientSex: 'female' as const,
					patientWeight: '65.8', // converted to kg
					medicalHistory: 'Pending collection',
				},
			},
			{
				delay: 13000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?\n\nCaller: The patient is a 67-year-old female, initials are M.R. She weighs about 145 pounds.\n\nAgent: Thank you. Now, can you tell me about the adverse event? What happened and when did it occur?",
				progress: 50,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
					patientAge: '67',
					patientSex: 'female' as const,
					patientWeight: '65.8',
					medicalHistory: 'Pending collection',
				},
			},
			{
				delay: 17000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?\n\nCaller: The patient is a 67-year-old female, initials are M.R. She weighs about 145 pounds.\n\nAgent: Thank you. Now, can you tell me about the adverse event? What happened and when did it occur?\n\nCaller: She developed severe cardiac arrhythmias approximately 3 hours after taking her morning dose of the study medication. The event occurred on March 15th. The arrhythmias were serious enough that we had to admit her to the ICU.",
				progress: 70,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
					patientAge: '67',
					patientSex: 'female' as const,
					patientWeight: '65.8',
					medicalHistory: 'Pending collection',
					eventDescription: 'Severe cardiac arrhythmias',
					eventOnsetDate: '2024-03-15',
					severity: 'severe' as const,
					seriousness: true,
					outcome: 'recovering' as const,
				},
			},
			{
				delay: 21000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?\n\nCaller: The patient is a 67-year-old female, initials are M.R. She weighs about 145 pounds.\n\nAgent: Thank you. Now, can you tell me about the adverse event? What happened and when did it occur?\n\nCaller: She developed severe cardiac arrhythmias approximately 3 hours after taking her morning dose of the study medication. The event occurred on March 15th. The arrhythmias were serious enough that we had to admit her to the ICU.\n\nAgent: I understand. Can you tell me about the medication she was taking? What was the product name, dose, and how was it administered?",
				progress: 80,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
					patientAge: '67',
					patientSex: 'female' as const,
					patientWeight: '65.8',
					medicalHistory: 'Pending collection',
					eventDescription: 'Severe cardiac arrhythmias',
					eventOnsetDate: '2024-03-15',
					severity: 'severe' as const,
					seriousness: true,
					outcome: 'recovering' as const,
				},
			},
			{
				delay: 25000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?\n\nCaller: The patient is a 67-year-old female, initials are M.R. She weighs about 145 pounds.\n\nAgent: Thank you. Now, can you tell me about the adverse event? What happened and when did it occur?\n\nCaller: She developed severe cardiac arrhythmias approximately 3 hours after taking her morning dose of the study medication. The event occurred on March 15th. The arrhythmias were serious enough that we had to admit her to the ICU.\n\nAgent: I understand. Can you tell me about the medication she was taking? What was the product name, dose, and how was it administered?\n\nCaller: She was taking CardioMax 150mg tablets, twice daily by mouth. This was for the clinical trial she's participating in for her heart condition.",
				progress: 95,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
					patientAge: '67',
					patientSex: 'female' as const,
					patientWeight: '65.8',
					medicalHistory: 'Heart condition (clinical trial participant)',
					eventDescription: 'Severe cardiac arrhythmias',
					eventOnsetDate: '2024-03-15',
					severity: 'severe' as const,
					seriousness: true,
					outcome: 'recovering' as const,
					productName: 'CardioMax',
					dose: '150mg',
					indication: 'Heart condition',
					therapyStartDate: '2024-03-10',
					routeOfAdministration: 'oral',
				},
			},
			{
				delay: 28000,
				transcript:
					"Agent: Thank you for calling the Adverse Event Hotline. My name is Sarah, and I'll be helping you today. Can you please tell me your name and relationship to the patient?\n\nCaller: Hi, my name is Dr. Jennifer Martinez. I'm a cardiologist at St. Mary's Hospital, and I'm calling about one of my patients.\n\nAgent: Thank you, Dr. Martinez. Can you please provide me with the patient's basic information - their age, gender, and initials?\n\nCaller: The patient is a 67-year-old female, initials are M.R. She weighs about 145 pounds.\n\nAgent: Thank you. Now, can you tell me about the adverse event? What happened and when did it occur?\n\nCaller: She developed severe cardiac arrhythmias approximately 3 hours after taking her morning dose of the study medication. The event occurred on March 15th. The arrhythmias were serious enough that we had to admit her to the ICU.\n\nAgent: I understand. Can you tell me about the medication she was taking? What was the product name, dose, and how was it administered?\n\nCaller: She was taking CardioMax 150mg tablets, twice daily by mouth. This was for the clinical trial she's participating in for her heart condition.\n\nAgent: Thank you for all this information, Dr. Martinez. I have captured all the key details for the adverse event report. Is there anything else you'd like to add about this case?",
				progress: 100,
				formData: {
					...initialData,
					reporterName: 'Dr. Jennifer Martinez',
					reporterType: 'healthcare_professional' as const,
					reporterContact: "St. Mary's Hospital - Cardiology Department",
					patientAge: '67',
					patientSex: 'female' as const,
					patientWeight: '65.8',
					medicalHistory: 'Heart condition (clinical trial participant)',
					eventDescription: 'Severe cardiac arrhythmias',
					eventOnsetDate: '2024-03-15',
					severity: 'severe' as const,
					seriousness: true,
					outcome: 'recovering' as const,
					productName: 'CardioMax',
					dose: '150mg',
					indication: 'Heart condition',
					therapyStartDate: '2024-03-10',
					routeOfAdministration: 'oral',
					causalityAssessment: 'probable' as const,
				},
			},
		]

		// Execute conversation steps
		conversationSteps.forEach((step, index) => {
			setTimeout(() => {
				setMockTranscript(step.transcript)
				setMockProgress(step.progress)
				setMockFormData(step.formData)

				// Auto-end conversation after completion
				if (index === conversationSteps.length - 1) {
					setTimeout(() => {
						setMockIsRecording(false)
						setMockIsCompleted(true)
						setShowAudioPlayback(true)
						// Also disconnect after a short pause
						setTimeout(() => {
							setMockIsConnected(false)
						}, 2000)
					}, 3000)
				}
			}, step.delay)
		})
	}, [
		setMockTranscript,
		setMockProgress,
		setMockFormData,
		setMockIsRecording,
		setMockIsConnected,
		setMockIsCompleted,
		setShowAudioPlayback,
		generateInitialFormData,
		isFinalMode,
	])

	const startMockConversation = useCallback(() => {
		// Don't restart if already completed
		if (mockIsCompleted) return

		setMockIsConnected(true)
		setMockIsRecording(true)
		setShowAudioPlayback(false)

		// Simulate the conversation progression
		simulateConversation()
	}, [simulateConversation, mockIsCompleted])

	const endMockConversation = useCallback(() => {
		// Only disconnect, don't reset data when completed
		if (mockIsCompleted) {
			setMockIsConnected(false)
			setMockIsRecording(false)
			return
		}

		// Full reset only if not completed
		setMockIsConnected(false)
		setMockIsRecording(false)
		setMockProgress(0)
		setMockTranscript('')
		setMockFormData(generateInitialFormData())
		setShowAudioPlayback(false)
	}, [generateInitialFormData, mockIsCompleted])

	const handleToggleCall = useCallback(async () => {
		// Only run on client side
		if (typeof window === 'undefined') {
			return
		}

		if (isMockMode) {
			// Mock mode toggle behavior
			if (mockIsConnected || mockIsRecording) {
				endMockConversation()
			} else {
				startMockConversation()
			}
		} else {
			// Real voice provider mode toggle behavior
			if (isConnected || isRecording) {
				// End the call if currently connected/recording
				endCall()
				toast.success('Voice conversation ended', {
					id: 'voice-connection',
				})
			} else if (isConnecting) {
				// Do nothing if already connecting to prevent multiple attempts
				toast.info('Voice agent is connecting, please wait...', {
					id: 'voice-connection',
				})
				return
			} else {
				// Start the call if not connected/connecting
				try {
					// Show loading toast
					toast.loading('Connecting to voice agent...', {
						id: 'voice-connection',
					})
					await startCall()
					// Success toast
					toast.success('Voice agent connected successfully!', {
						id: 'voice-connection',
					})
				} catch (error) {
					// Error toast with specific guidance
					let errorMessage = 'Failed to connect to voice agent.'

					if (error instanceof Error) {
						if (
							error.message.includes('microphone') ||
							error.message.includes('audio')
						) {
							errorMessage =
								'Microphone access required. Please allow microphone permissions and try again.'
						} else if (
							error.message.includes('Agent ID') ||
							error.message.includes('agent')
						) {
							errorMessage =
								'Voice agent not configured. Please check your settings.'
						} else if (
							error.message.includes('SDK') ||
							error.message.includes('module')
						) {
							errorMessage =
								'Voice service temporarily unavailable. Please try again later.'
						} else if (
							error.message.includes('network') ||
							error.message.includes('connection')
						) {
							errorMessage =
								'Network connection error. Please check your internet connection.'
						}
					}

					toast.error(errorMessage, {
						id: 'voice-connection',
						duration: 8000,
						action: {
							label: 'Try Again',
							onClick: () => handleToggleCall(),
						},
					})
				}
			}
		}
	}, [
		isMockMode,
		mockIsConnected,
		mockIsRecording,
		endMockConversation,
		startMockConversation,
		isConnected,
		isRecording,
		isConnecting,
		endCall,
		startCall,
	])

	// Auto-start mock conversation when in mock mode (but not if already completed)
	useEffect(() => {
		if (isMockMode && !mockIsConnected && !mockIsCompleted) {
			// Auto-start after a short delay
			const timer = setTimeout(() => {
				startMockConversation()
			}, 1000)
			return () => clearTimeout(timer)
		}
	}, [isMockMode, mockIsConnected, mockIsCompleted, startMockConversation])

	// Listen for voice connection errors and show toast notifications
	useEffect(() => {
		if (error && !isMockMode) {
			toast.error(error, {
				id: 'voice-error',
				duration: 8000,
				action: {
					label: 'Try Again',
					onClick: () => handleToggleCall(),
				},
			})
		}
	}, [error, isMockMode, handleToggleCall])

	// Use mock data when in mock mode
	const displayIsConnected = isMockMode ? mockIsConnected : isConnected
	const displayIsRecording = isMockMode ? mockIsRecording : isRecording
	const displayTranscript = isMockMode ? mockTranscript : transcript
	const displayFormData: ExtendedFormData = isMockMode ? mockFormData : formData

	return (
		<div className="flex min-h-screen flex-col overflow-auto bg-gradient-to-br from-slate-50 to-slate-100 lg:h-screen lg:max-h-screen lg:flex-row lg:overflow-hidden">
			{/* Left Panel - Voice Interface */}
			<div className="relative flex min-h-[50vh] flex-1 flex-col items-center justify-center p-3 py-6 lg:min-h-0 lg:p-8">
				{/* Main Voice Button */}
				<div className="flex flex-col items-center space-y-6">
					<VoiceButton
						onClick={handleToggleCall}
						isActive={displayIsConnected || displayIsRecording}
						isConnecting={!isMockMode && isConnecting}
						text={
							!isMockMode && isConnecting
								? 'Connecting...'
								: displayIsConnected || displayIsRecording
									? 'Push to end conversation'
									: isMockMode
										? isFinalMode || mockIsCompleted
											? 'Conversation completed'
											: 'Push to start conversation'
										: 'Push to start conversation'
						}
					/>
				</div>

				{/* Audio Settings */}
				<div className="absolute right-4 bottom-4 lg:right-8 lg:bottom-8">
					<AudioSettings />
				</div>
			</div>

			{/* Right Panel - Information Tabs */}
			<div className="flex min-h-[50vh] flex-1 flex-col border-t border-neutral-200 bg-white lg:h-screen lg:border-t-0 lg:border-l">
				{/* Recording Banner - Always visible to prevent content shifting */}
				<div
					className={`flex flex-shrink-0 items-center justify-center space-x-2 border-b p-3 transition-colors duration-200 ${
						displayIsRecording ? 'bg-red-50' : 'bg-neutral-50/50'
					}`}
				>
					{displayIsRecording ? (
						<>
							<div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
							<span className="text-sm font-medium text-red-700">
								Recording
							</span>
						</>
					) : (
						<>
							<div className="h-2 w-2 rounded-full bg-neutral-300" />
							<span className="text-muted-foreground text-sm font-medium">
								Not Recording
							</span>
						</>
					)}
				</div>

				<Tabs
					defaultValue="report"
					className="flex h-full flex-col overflow-hidden"
				>
					<TabsList className="grid w-full flex-shrink-0 grid-cols-2 rounded-none border-b">
						<TabsTrigger value="report" className="rounded-none py-3">
							Report
						</TabsTrigger>
						<TabsTrigger value="transcript" className="rounded-none py-3">
							Transcript
							{showAudioPlayback && (
								<Badge className="ml-2 bg-green-100 text-xs text-green-800">
									Completed
								</Badge>
							)}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="report" className="flex-1 overflow-hidden p-0">
						<Report formData={displayFormData} />
					</TabsContent>

					<TabsContent
						value="transcript"
						className="flex-1 overflow-hidden p-0"
					>
						<LiveTranscript
							transcript={displayTranscript}
							isRecording={displayIsRecording}
							showAudioPlayback={showAudioPlayback}
							mockAudioState={mockAudioState}
							setMockAudioState={setMockAudioState}
						/>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	)
}

interface VoiceButtonProps {
	onClick: () => void
	isActive: boolean
	isConnecting?: boolean
	text: string
}

function VoiceButton({
	onClick,
	isActive,
	isConnecting = false,
	text,
}: VoiceButtonProps) {
	const isDisabled = isConnecting

	const handleClick = () => {
		if (!isDisabled && onClick) {
			onClick()
		}
	}

	return (
		<div className="flex flex-col items-center space-y-4">
			<Button
				onClick={handleClick}
				disabled={isDisabled}
				className={`group relative h-24 w-24 cursor-pointer overflow-hidden rounded-full border-4 transition-all duration-300 lg:h-32 lg:w-32 ${
					isDisabled
						? 'text-muted-foreground cursor-not-allowed border-neutral-200 bg-neutral-100'
						: isActive
							? 'text-muted-foreground border-neutral-300 bg-white hover:border-red-400 hover:bg-red-500 hover:text-white'
							: 'text-foreground border-neutral-200 bg-white/80 hover:bg-white/90'
				} backdrop-blur-md`}
			>
				{/* Background waveform - always present */}
				<div
					className={`absolute inset-0 flex items-center justify-center ${
						isActive ? 'opacity-60' : 'opacity-25'
					}`}
					style={{ transform: 'translateX(4px)' }}
				>
					<Waveform isActive={isActive} />
				</div>

				{/* Default icon */}
				<div
					className={`relative z-10 flex flex-col items-center space-y-2 transition-opacity duration-200 ${
						isActive ? 'group-hover:opacity-0' : ''
					}`}
				>
					{isConnecting ? (
						<div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-600" />
					) : isActive ? (
						<div className="h-8 w-8" />
					) : (
						<Mic className="h-8 w-8" />
					)}
				</div>

				{/* Hover overlay for active state */}
				{isActive && (
					<div className="absolute inset-0 z-20 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
						<Square className="h-8 w-8" />
					</div>
				)}
			</Button>
			<p
				className={`text-lg font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}
			>
				{text}
			</p>
		</div>
	)
}

function Waveform({ isActive = false }: { isActive?: boolean }) {
	return (
		<div className="flex h-16 w-full items-center justify-center space-x-1.5">
			{[...Array(7)].map((_, i) => (
				<div
					key={i}
					className={`w-1.5 rounded-full transition-all duration-500 ease-in-out ${
						isActive ? 'bg-black/80' : 'bg-white/50'
					}`}
					style={{
						height: `${Math.random() * 36 + 12}px`,
						animation: 'waveform 1.5s ease-in-out infinite',
						animationDelay: `${i * 150}ms`,
					}}
				/>
			))}
			<style>{`
				@keyframes waveform {
					0%,
					100% {
						transform: scaleY(0.5);
					}
					50% {
						transform: scaleY(1);
					}
				}
			`}</style>
		</div>
	)
}

function AudioSettings() {
	const [audioQuality, setAudioQuality] = useState('hd')
	const [language, setLanguage] = useState('en-us')

	const audioQualities = [
		{ value: 'standard', label: 'Standard' },
		{ value: 'hd', label: 'HD' },
		{ value: 'ultra-hd', label: 'Ultra HD' },
	]

	const languages = [
		{ value: 'en-us', label: 'English (US)' },
		{ value: 'en-uk', label: 'English (UK)' },
		{ value: 'es-es', label: 'Spanish' },
		{ value: 'fr-fr', label: 'French' },
		{ value: 'de-de', label: 'German' },
		{ value: 'it-it', label: 'Italian' },
		{ value: 'pt-br', label: 'Portuguese' },
		{ value: 'ja-jp', label: 'Japanese' },
		{ value: 'ko-kr', label: 'Korean' },
		{ value: 'zh-cn', label: 'Chinese (Simplified)' },
	]

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant="ghost" size="sm" className="h-8 w-8 rounded-full p-0">
					<HelpCircle className="text-muted-foreground h-4 w-4" />
				</Button>
			</PopoverTrigger>
			<PopoverContent side="top" className="w-80">
				<div className="space-y-4 text-sm">
					<div className="space-y-2">
						<label className="text-sm font-medium">Audio Quality</label>
						<Select value={audioQuality} onValueChange={setAudioQuality}>
							<SelectTrigger className="h-8">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{audioQualities.map((quality) => (
									<SelectItem key={quality.value} value={quality.value}>
										{quality.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-2">
						<label className="text-sm font-medium">Language</label>
						<Select value={language} onValueChange={setLanguage}>
							<SelectTrigger className="h-8">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{languages.map((lang) => (
									<SelectItem key={lang.value} value={lang.value}>
										{lang.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}

interface LiveTranscriptProps {
	transcript: string
	isRecording: boolean
	showAudioPlayback?: boolean
	mockAudioState?: {
		isPlaying: boolean
		currentTime: number
		duration: number
	}
	setMockAudioState?: (state: {
		isPlaying: boolean
		currentTime: number
		duration: number
	}) => void
}

function LiveTranscript({
	transcript,
	isRecording,
	showAudioPlayback = false,
	mockAudioState,
	setMockAudioState,
}: LiveTranscriptProps) {
	const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0)
	const [isPlaying, setIsPlaying] = useState(false)
	const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
		null,
	)

	// Mock function to generate word timings for demo purposes
	const generateWordTimings = (text: string, baseTime: number) => {
		const words = text.split(/\s+/).filter((word) => word.length > 0)
		const timings: { word: string; startTime: number; endTime: number }[] = []
		let currentTime = baseTime

		words.forEach((word) => {
			// Simulate realistic word timing (150-300ms per word + pauses)
			const wordDuration = Math.max(150, word.length * 80 + Math.random() * 100)
			const pauseDuration = Math.random() * 200 + 50 // Natural pauses between words

			timings.push({
				word: word,
				startTime: currentTime,
				endTime: currentTime + wordDuration,
			})

			currentTime += wordDuration + pauseDuration
		})

		return timings
	}

	// Parse transcript into segments with timestamps
	const parseTranscript = (text: string): SimpleTranscriptSegment[] => {
		if (!text) return []

		// Split by speaker indicators and create segments
		const lines = text.split('\n').filter((line) => line.trim())
		const segments: SimpleTranscriptSegment[] = []
		let currentSegment: Partial<SimpleTranscriptSegment> | null = null

		// Map conversation steps to their actual timing delays from the mock conversation
		const conversationTimings = [
			{ delay: 0, speaker: 'Agent' }, // First agent message at 2s (but we start at 0)
			{ delay: 2000, speaker: 'Caller' }, // First caller response at 4s
			{ delay: 5000, speaker: 'Agent' }, // Second agent message at 7s
			{ delay: 8000, speaker: 'Caller' }, // Second caller response at 10s
			{ delay: 11000, speaker: 'Agent' }, // Third agent message at 13s
			{ delay: 15000, speaker: 'Caller' }, // Third caller response at 17s
			{ delay: 19000, speaker: 'Agent' }, // Fourth agent message at 21s
			{ delay: 23000, speaker: 'Caller' }, // Fourth caller response at 25s
			{ delay: 26000, speaker: 'Agent' }, // Final agent message at 28s
		]

		let segmentIndex = 0

		for (const line of lines) {
			// Check if line starts with a speaker indicator
			const speakerMatch = line.match(
				/^(Agent|Patient|Reporter|Caller):\s*(.*)/,
			)
			if (speakerMatch) {
				// Save previous segment
				if (currentSegment && currentSegment.speaker && currentSegment.text) {
					segments.push(currentSegment as SimpleTranscriptSegment)
					segmentIndex++
				}

				// Start new segment with proper timing from conversation steps
				const segmentText = speakerMatch[2] || ''
				const timing = conversationTimings[segmentIndex] || {
					delay: segmentIndex * 3000,
					speaker: speakerMatch[1],
				}
				const startTime = timing.delay

				const words = segmentText
					? generateWordTimings(segmentText, startTime)
					: []

				// Calculate simple timestamp display (minutes:seconds)
				const minutes = Math.floor(startTime / 60000)
				const seconds = Math.floor((startTime % 60000) / 1000)
				const timestamp = `${minutes}:${seconds.toString().padStart(2, '0')}`

				currentSegment = {
					speaker: speakerMatch[1] || '',
					text: segmentText,
					isAgent: speakerMatch[1] === 'Agent',
					words: words,
					segmentStartTime: startTime,
					timestamp: timestamp,
				}
			} else if (currentSegment) {
				// Add to current segment
				const additionalText = (currentSegment.text ? ' ' : '') + line
				currentSegment.text = additionalText
				// Regenerate word timings for the complete text
				if (currentSegment.segmentStartTime !== undefined) {
					currentSegment.words = generateWordTimings(
						additionalText,
						currentSegment.segmentStartTime,
					)
				}
			}
		}

		// Add final segment
		if (currentSegment && currentSegment.speaker && currentSegment.text) {
			segments.push(currentSegment as SimpleTranscriptSegment)
		}

		return segments
	}

	const segments = parseTranscript(transcript)

	// Calculate display values for playback
	const displayCurrentTime =
		showAudioPlayback && mockAudioState
			? mockAudioState.currentTime
			: currentPlaybackTime
	const displayIsPlaying =
		showAudioPlayback && mockAudioState ? mockAudioState.isPlaying : isPlaying

	// Handle audio player time update
	const handleAudioTimeUpdate = (time: number) => {
		if (mockAudioState && setMockAudioState) {
			setMockAudioState({
				...mockAudioState,
				currentTime: time,
			})
		}
	}

	// Handle audio player play state change
	const handleAudioPlayStateChange = (playing: boolean) => {
		if (mockAudioState && setMockAudioState) {
			setMockAudioState({
				...mockAudioState,
				isPlaying: playing,
			})
		}
	}

	// Set recording start time when recording begins
	useEffect(() => {
		if (isRecording && recordingStartTime === null) {
			setRecordingStartTime(Date.now())
		} else if (!isRecording) {
			setRecordingStartTime(null)
			setCurrentPlaybackTime(0)
			setIsPlaying(false)
		}
	}, [isRecording, recordingStartTime])

	// Simulate audio playback timing when recording
	useEffect(() => {
		if (!isRecording || !recordingStartTime || segments.length === 0) {
			setIsPlaying(false)
			return
		}

		setIsPlaying(true)

		const updatePlaybackTime = () => {
			const elapsed = Date.now() - recordingStartTime
			setCurrentPlaybackTime(elapsed)

			// Continue updating while recording
			if (isRecording) {
				requestAnimationFrame(updatePlaybackTime)
			}
		}

		const animationFrame = requestAnimationFrame(updatePlaybackTime)

		return () => {
			cancelAnimationFrame(animationFrame)
		}
	}, [isRecording, recordingStartTime, segments.length])

	return (
		<div className="flex h-full flex-col overflow-hidden">
			{/* Audio Playback Controls - Mobile Only (Top) */}
			{showAudioPlayback && mockAudioState && (
				<div className="flex-shrink-0 border-b p-4 lg:hidden">
					<AudioPlayer
						duration={mockAudioState.duration}
						onTimeUpdate={handleAudioTimeUpdate}
						onPlayStateChange={handleAudioPlayStateChange}
						className="pb-2"
						showSkipButtons={true}
						skipAmount={5}
					/>
				</div>
			)}

			{/* Transcript */}
			<div className="flex-1 overflow-hidden">
				<SimpleTranscript
					segments={segments}
					currentTime={displayCurrentTime}
					isPlaying={displayIsPlaying}
					showTimestamps={true}
				/>
			</div>

			{/* Audio Playback Controls - Desktop Only (Bottom) */}
			{showAudioPlayback && mockAudioState && (
				<div className="bg-background hidden flex-shrink-0 border-t p-4 lg:block">
					<AudioPlayer
						duration={mockAudioState.duration}
						onTimeUpdate={handleAudioTimeUpdate}
						onPlayStateChange={handleAudioPlayStateChange}
						className="pb-2"
						showSkipButtons={true}
						skipAmount={5}
					/>
				</div>
			)}
		</div>
	)
}

interface ReportProps {
	formData: ExtendedFormData
}

function Report({ formData }: ReportProps) {
	// Calculate progress based on required fields
	// Only include fields that should be collected from patients during the call
	const sections = [
		{
			id: 'reporter',
			title: 'Reporter Information',
			fields: [
				{
					id: 'reporterName',
					label: 'Reporter Name',
					value: formData.reporterName,
					required: true,
				},
				{
					id: 'reporterType',
					label: 'Reporter Type',
					value: formData.reporterType,
					required: true,
				},
				{
					id: 'reporterContact',
					label: 'Contact Information',
					value: formData.reporterContact,
					required: true,
				},
			],
		},
		{
			id: 'patient',
			title: 'Patient Information',
			fields: [
				{
					id: 'patientAge',
					label: 'Age',
					value: formData.patientAge,
					required: true,
				},
				{
					id: 'patientSex',
					label: 'Sex',
					value: formData.patientSex,
					required: true,
				},
				{
					id: 'patientWeight',
					label: 'Weight',
					value: formData.patientWeight,
					required: false,
				},
				{
					id: 'medicalHistory',
					label: 'Medical History',
					value:
						formData.medicalHistoryItems &&
						formData.medicalHistoryItems.length > 0
							? `${formData.medicalHistoryItems.length} conditions`
							: formData.medicalHistory || undefined,
					required: false,
					isStructured:
						formData.medicalHistoryItems &&
						formData.medicalHistoryItems.length > 0,
					structuredData: formData.medicalHistoryItems,
				},
				{
					id: 'concomitantMedications',
					label: 'Concomitant Medications',
					value:
						formData.concomitantMedications &&
						formData.concomitantMedications.length > 0
							? `${formData.concomitantMedications.length} medications`
							: undefined,
					required: false,
					isStructured:
						formData.concomitantMedications &&
						formData.concomitantMedications.length > 0,
					structuredData: formData.concomitantMedications,
				},
			],
		},
		{
			id: 'event',
			title: 'Adverse Event Details',
			fields: [
				{
					id: 'eventDescription',
					label: 'What Happened',
					value: formData.eventDescription,
					required: true,
				},
				{
					id: 'eventOnsetDate',
					label: 'When Did It Start',
					value: formData.eventOnsetDate,
					required: true,
				},
				{
					id: 'outcome',
					label: 'Current Status',
					value: formData.outcome,
					required: true,
				},
			],
		},
		{
			id: 'drug',
			title: 'Suspect Drug',
			fields: [
				{
					id: 'productName',
					label: 'Suspect Drug Name',
					value: formData.productName,
					required: true,
				},
				{
					id: 'dose',
					label: 'Dose & Frequency',
					value: formData.dose,
					required: true,
				},
				{
					id: 'indication',
					label: 'Indication',
					value: formData.indication,
					required: true,
				},
				{
					id: 'therapyStartDate',
					label: 'Start Date',
					value: formData.therapyStartDate,
					required: false,
				},
				{
					id: 'routeOfAdministration',
					label: 'Route of Administration',
					value: formData.routeOfAdministration,
					required: true,
				},
			],
		},
	]

	// Calculate field counts
	const allFields = sections.flatMap((section) => section.fields)
	const requiredFields = allFields.filter((field) => field.required)
	const optionalFields = allFields.filter((field) => !field.required)
	const filledRequiredFields = requiredFields.filter((field) => field.value)
	const filledOptionalFields = optionalFields.filter((field) => field.value)

	// Calculate progress percentage based on required fields
	const progressPercentage =
		requiredFields.length > 0
			? Math.round((filledRequiredFields.length / requiredFields.length) * 100)
			: 0

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<div className="flex-shrink-0 p-6">
				<div className="mb-3 flex items-center justify-between">
					<span className="text-foreground text-sm font-medium">Progress</span>
					<Badge variant="outline">{progressPercentage}%</Badge>
				</div>
				<Progress value={progressPercentage} className="h-2" />
				<div className="text-muted-foreground mt-2 flex justify-between text-xs">
					<span>
						Required Fields: {filledRequiredFields.length}/
						{requiredFields.length}
					</span>
					<span>
						Optional Fields: {filledOptionalFields.length}/
						{optionalFields.length}
					</span>
				</div>
			</div>

			<div className="flex-1 overflow-hidden">
				<ScrollArea className="h-full px-6">
					<DataAccordionSections sections={sections} />
				</ScrollArea>
			</div>
		</div>
	)
}

function DataAccordionSections({
	sections,
}: {
	sections: Array<{
		id: string
		title: string
		fields: Array<{
			id: string
			label: string
			value: string | boolean | Date | undefined
			required: boolean
			isStructured?: boolean
			structuredData?:
				| SimpleMedicalHistoryItem[]
				| SimpleConcomitantMedication[]
		}>
	}>
}) {
	const [openSections, setOpenSections] = useState<Record<string, boolean>>({
		reporter: true,
		patient: false,
		event: false,
		drug: false,
	})

	const toggleSection = (sectionId: string) => {
		setOpenSections((prev) => ({
			...prev,
			[sectionId]: !prev[sectionId],
		}))
	}

	return (
		<div className="space-y-2">
			{sections.map((section) => {
				const filledFields = section.fields.filter((f) => f.value).length
				const totalFields = section.fields.length
				const requiredFields = section.fields.filter((f) => f.required).length
				const filledRequiredFields = section.fields.filter(
					(f) => f.required && f.value,
				).length

				return (
					<Collapsible
						key={section.id}
						open={openSections[section.id]}
						onOpenChange={() => toggleSection(section.id)}
					>
						<CollapsibleTrigger className="w-full">
							<div className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-neutral-50">
								<div className="flex items-center space-x-3">
									{openSections[section.id] ? (
										<ChevronDown className="text-muted-foreground h-4 w-4" />
									) : (
										<ChevronRight className="text-muted-foreground h-4 w-4" />
									)}
									<span className="text-left font-medium">{section.title}</span>
								</div>
								<div className="flex items-center space-x-2">
									<Badge variant="outline" className="text-xs">
										{filledRequiredFields}/{requiredFields} required
									</Badge>
									<Badge variant="secondary" className="text-xs">
										{filledFields}/{totalFields} total
									</Badge>
								</div>
							</div>
						</CollapsibleTrigger>

						<CollapsibleContent>
							<div className="ml-6 space-y-3 border-l-2 border-neutral-100 py-2 pl-4">
								{section.fields.map((field) => {
									// Determine the appropriate icon
									const getFieldIcon = () => {
										if (field.value) {
											// Field has a value - green checkmark
											return <CheckCircle2 className="h-4 w-4 text-green-500" />
										} else if (field.required) {
											// Required field without value - red X
											return <XCircle className="h-4 w-4 text-red-500" />
										} else {
											// Optional field without value - yellow warning
											return <AlertCircle className="h-4 w-4 text-yellow-500" />
										}
									}

									return (
										<div
											key={field.id}
											className="flex items-center justify-between py-2"
										>
											<div className="flex items-center space-x-2">
												<span className="text-muted-foreground text-sm">
													{field.label}
												</span>
												{field.required && (
													<span className="text-xs text-red-500">*</span>
												)}
											</div>
											<div className="flex items-center space-x-3">
												<div className="text-sm font-medium">
													{field.value ? (
														field.isStructured ? (
															<Sheet>
																<SheetTrigger asChild>
																	<button className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline">
																		{formatFieldValue(field.value)}
																	</button>
																</SheetTrigger>
																<SheetContent className="w-[400px] sm:w-[540px]">
																	<SheetHeader>
																		<SheetTitle>{field.label}</SheetTitle>
																		<SheetDescription>
																			Detailed information collected during the
																			conversation
																		</SheetDescription>
																	</SheetHeader>
																	<div className="px-4">
																		{field.id === 'medicalHistory' &&
																		field.structuredData
																			? // <MedicalHistoryTimeline
																				// 	data={
																				// 		field.structuredData as SimpleMedicalHistoryItem[]
																				// 	}
																				// />
																				null
																			: field.id === 'concomitantMedications' &&
																				  field.structuredData
																				? // <MedicationsTimeline
																					// 	data={
																					// 		field.structuredData as SimpleConcomitantMedication[]
																					// 	}
																					// />
																					null
																				: null}
																	</div>
																</SheetContent>
															</Sheet>
														) : (
															<span className="text-foreground">
																{formatFieldValue(field.value)}
															</span>
														)
													) : (
														<span className="text-muted-foreground italic">
															Not collected
														</span>
													)}
												</div>
												{getFieldIcon()}
											</div>
										</div>
									)
								})}
							</div>
						</CollapsibleContent>
					</Collapsible>
				)
			})}
		</div>
	)
}

function formatFieldValue(value: string | boolean | Date | undefined): string {
	if (value === undefined || value === null) {
		return ''
	}

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No'
	}

	if (value instanceof Date) {
		return value.toLocaleDateString()
	}

	if (typeof value === 'string') {
		return value.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
	}

	return String(value)
}
